/* === SERENE HEAT — scroll reveal, parallax, cart-icon bounce ===
   Vanilla JS, no deps. Loaded site-wide via theme.liquid.
   Respects prefers-reduced-motion. Fails silent if APIs unsupported. */

(function () {
  'use strict';

  var doc = document;
  var win = window;
  var reduceMotion = win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     1. Scroll reveal — IntersectionObserver
     ------------------------------------------------------------------ */
  function initReveal() {
    var targets = doc.querySelectorAll('[data-serene-reveal]');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in win)) {
      // Skip animation: just show everything.
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    // Stagger siblings inside the same parent so groups feel rhythmic.
    var staggerMap = new WeakMap();
    targets.forEach(function (el) {
      var parent = el.parentElement;
      if (!parent) return;
      // count this el's index among siblings that also have data-serene-reveal
      var siblings = parent.querySelectorAll(':scope > [data-serene-reveal]');
      if (siblings.length > 1) {
        for (var i = 0; i < siblings.length; i++) {
          if (siblings[i] === el) {
            staggerMap.set(el, i);
            break;
          }
        }
      }
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var explicit = parseInt(el.getAttribute('data-serene-reveal-delay'), 10);
        var idx = staggerMap.has(el) ? staggerMap.get(el) : 0;
        var delay = isNaN(explicit) ? idx * 60 : explicit;
        if (delay > 0) {
          el.style.transitionDelay = delay + 'ms';
        }
        // double rAF so transition definitely fires
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.classList.add('is-revealed');
          });
        });
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------
     2. Hero parallax — subtle vertical drift on scroll
     ------------------------------------------------------------------ */
  function initParallax() {
    if (reduceMotion) return;
    var heroes = doc.querySelectorAll('[data-serene-parallax]');
    if (!heroes.length) return;

    var ticking = false;
    function update() {
      var y = win.pageYOffset || doc.documentElement.scrollTop;
      heroes.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > win.innerHeight) return;
        var speed = parseFloat(el.getAttribute('data-serene-parallax')) || 0.18;
        var offset = Math.round(y * speed * -1);
        el.style.setProperty('--parallax-y', offset + 'px');
      });
      ticking = false;
    }
    win.addEventListener('scroll', function () {
      if (!ticking) {
        win.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------
     3. Cart icon bounce — when [data-serene-cart-count] text changes
     ------------------------------------------------------------------ */
  function initCartBounce() {
    if (reduceMotion) return;
    var counters = doc.querySelectorAll('[data-serene-cart-count]');
    if (!counters.length || !('MutationObserver' in win)) return;

    counters.forEach(function (el) {
      var pill = el.closest('.nav-cta') || el.parentElement;
      var lastVal = el.textContent.trim();
      var mo = new MutationObserver(function () {
        var v = el.textContent.trim();
        if (v === lastVal) return;
        lastVal = v;
        if (!pill) return;
        pill.classList.remove('is-bouncing');
        // force reflow so the class re-adds and replays
        void pill.offsetWidth;
        pill.classList.add('is-bouncing');
        setTimeout(function () {
          pill.classList.remove('is-bouncing');
        }, 600);
      });
      mo.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ------------------------------------------------------------------
     4. Mobile slide-down nav with staggered link reveal
     ------------------------------------------------------------------ */
  function initMobileNav() {
    var hamburger = doc.querySelector('.serene-nav-section .hamburger');
    var navLinks = doc.querySelector('.serene-nav-section .nav-links');
    if (!hamburger || !navLinks) return;

    // The original handler in serene-header.liquid still toggles .open.
    // We just enhance with the staggered reveal — listen for class changes.
    var mo = new MutationObserver(function () {
      if (navLinks.classList.contains('open')) {
        var links = navLinks.querySelectorAll('a');
        links.forEach(function (a, i) {
          a.style.transitionDelay = (i * 50) + 'ms';
        });
      } else {
        // reset on close so the next open re-runs cleanly
        var links2 = navLinks.querySelectorAll('a');
        links2.forEach(function (a) { a.style.transitionDelay = ''; });
      }
    });
    mo.observe(navLinks, { attributes: true, attributeFilter: ['class'] });
  }

  /* ------------------------------------------------------------------
     5. Page transition fade — fade body briefly on internal link click
     ------------------------------------------------------------------ */
  function initPageFade() {
    if (reduceMotion) return;
    doc.body.classList.add('serene-fade-in');
    doc.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      // Skip: new tab, modifier keys, hash links, mailto/tel, external, no-fade opt-out
      if (a.target === '_blank') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.hasAttribute('download')) return;
      if (a.dataset.noFade !== undefined) return;
      var href = a.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#') return;
      if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      try {
        var url = new URL(a.href, location.href);
        if (url.origin !== location.origin) return;
        if (url.pathname === location.pathname && url.search === location.search) return;
      } catch (_) { return; }
      doc.body.classList.add('serene-fading-out');
    });
    win.addEventListener('pageshow', function () {
      doc.body.classList.remove('serene-fading-out');
    });
  }

  function init() {
    initReveal();
    initParallax();
    initCartBounce();
    initMobileNav();
    initPageFade();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
